import { getAppPermissionsState, resolveMemberType, } from '../comments/comments-permissions';
import { refetchCommentsData } from '../comments/paging/comments-paging-actions';
import { unreachable } from '../../../ts-utils';
import { getLanguage } from '../base-params/base-params-selectors';
import { biAttemptLoginSignup, biCaptchaEvent } from '../actions/bi-actions';
import { isEqual } from 'lodash';
import { getCurrentOwner } from './members-selectors';
const returnWithDelay = (value) => new Promise((res) => setTimeout(() => res(value), 1000));
/*
 * We have refetchCommentsData triggered automatically on login, but when we
 * trigger authentication on action we can not get result of refetchCommentsData.
 * So we must do the call, but we don not want double fetch.
 * So the solution to this is to have flag in state, which can mute automatically triggered
 * refetch. Other solutions include some middleware, so skiping for now.
 */
export const PERMISSION_CHECK_START = 'PERMISSION_CHECK_START';
export const PERMISSION_CHECK_END = 'PERMISSION_CHECK_END';
export const permissionCheckStart = () => ({ type: PERMISSION_CHECK_START });
export const permissionCheckEnd = () => ({ type: PERMISSION_CHECK_END });
// This thunk checks permissions and if needed asks user to authenticate
// Returns true if permission for action acquired and false if not.
export function ensureMemberHasRightTo(params) {
    return runInBand(async (dispatch, getState, { wixCodeApi, actionPreconditions, testMode }) => {
        let lastPemissionState;
        async function continueEnsure() {
            const actionPermissionState = getActionPermission();
            if (isEqual(lastPemissionState, actionPermissionState)) {
                // There is some missconfig, because the state did not change, so we prevent infinite loop
                console.error('No change in permissions/member after permission lift, some missconfiguration must have happened', actionPermissionState);
                return Promise.resolve('INSUFFICIENT_PERMISSIONS');
            }
            lastPemissionState = actionPermissionState;
            if (actionPermissionState?.type !== 'ALLOW') {
                return Promise.resolve('INSUFFICIENT_PERMISSIONS');
            }
            const { precondition } = actionPermissionState;
            switch (precondition) {
                case 'NONE':
                    return Promise.resolve('OK');
                case 'CONSUMER_PRECONDITION': {
                    const memberType = resolveMemberType(getCurrentOwner(getState()));
                    return consumerPrecondition(params.resourceId, memberType);
                }
                case 'VISITOR_TO_MEMBER':
                    dispatch(biAttemptLoginSignup({ type: 'unknow', resourceId: params.resourceId }));
                    return visitorToMember();
                case 'GUEST_FLOW':
                    if ('isGuestFlowChosen' in params && params.isGuestFlowChosen) {
                        const showCaptcha = () => {
                            dispatch(biCaptchaEvent({
                                step: 'SHOW',
                                resourceId: params.resourceId,
                                userAction: params.userAction,
                            }));
                            return (wixCodeApi.authentication
                                .openCaptchaChallenge()
                                .then((token) => {
                                // Not sure if second path is available - if you fail you
                                // are just given a new test infinitely (or at least 10 times -
                                // didn't have energy to try more)
                                return token ? 'OK' : 'CANCEL';
                            })
                                // If captcha is closed this promise rejects
                                .catch(() => 'CANCEL'));
                        };
                        const captchaResult = await (testMode.skipCaptcha
                            ? (() => {
                                showCaptcha();
                                return returnWithDelay('OK');
                            })()
                            : showCaptcha());
                        dispatch(biCaptchaEvent({
                            step: 'COMPLETE',
                            isSuccess: captchaResult === 'OK',
                            resourceId: params.resourceId,
                            userAction: params.userAction,
                        }));
                        return captchaResult === 'OK' ? 'OK' : 'ACTION_CANCELLED';
                    }
                    else {
                        dispatch(biAttemptLoginSignup({ type: 'unknow', resourceId: params.resourceId }));
                        return visitorToMember();
                    }
                default:
                    throw unreachable(precondition);
            }
        }
        return continueEnsure();
        function getActionPermission() {
            const listState = getState().comments[params.resourceId];
            if (listState?.type !== 'READY') {
                return undefined;
            }
            const appPermissionState = getAppPermissionsState({
                // TODO: this check should already be verified from component side, so so we mock it to false
                // If more flags like this appear, we should consider syncing component configuration to redux
                isLocked: false,
                permissions: listState.configs.permissions,
                currentOwner: getCurrentOwner(getState()),
                srcResourceId: params.resourceId,
                isPreconditionRequiredForAction: actionPreconditions?.isPreconditionRequiredForAction,
            });
            if (params.userAction !== 'UPDATE' && params.userAction !== 'DELETE') {
                return appPermissionState[params.userAction];
            }
            const commentState = listState.commentsById[params.commentId];
            if (!('comment' in commentState)) {
                return undefined;
            }
            return appPermissionState[params.userAction](commentState.comment);
        }
        async function consumerPrecondition(resourceId, memberType) {
            await actionPreconditions?.preconditionCallback(params.userAction, {
                srcResourceId: resourceId,
                memberType,
            });
            if (actionPreconditions?.isPreconditionRequiredForAction(params.userAction, {
                srcResourceId: resourceId,
                memberType,
            })) {
                return 'ACTION_CANCELLED';
            }
            await dispatch(refetchCommentsData());
            return continueEnsure();
        }
        async function visitorToMember() {
            dispatch(permissionCheckStart()); // Block auto refetch in onLogin callback
            if (!(await showLoginPopup({ wixCodeApi, lang: getLanguage(getState()) }))) {
                dispatch(permissionCheckEnd());
                return 'ACTION_CANCELLED';
            }
            await dispatch(refetchCommentsData());
            dispatch(permissionCheckEnd());
            return continueEnsure();
        }
    });
}
export const showLoginPopup = ({ wixCodeApi, lang }) => wixCodeApi.user
    .promptLogin({
    lang,
    modal: true,
})
    .catch(() => undefined);
// This little monster ensures that rights lifting would not be run in parallel.
// If few elements will trigger the sign-up or 'become public' member modal it's important
// that the modals would show only once per such series, and 'ACTION_CANCELLED' is
// passed to all following calls, so sign-up wouldn't show repeatedly.
let promiseQueue = Promise.resolve();
let isRunning = false;
function runInBand(fn) {
    return (...args) => {
        return new Promise((resolve, reject) => {
            const executePermissionCheck = () => {
                return fn(...args)
                    .then((result) => {
                    resolve(result);
                    isRunning = false;
                    return result;
                })
                    .catch(reject);
            };
            if (!isRunning) {
                isRunning = true;
                promiseQueue = promiseQueue.then(executePermissionCheck);
            }
            else {
                promiseQueue = promiseQueue.then((previousResult) => {
                    if (previousResult === 'ACTION_CANCELLED') {
                        resolve('ACTION_CANCELLED');
                        return 'ACTION_CANCELLED';
                    }
                    else {
                        isRunning = true;
                        return executePermissionCheck();
                    }
                });
            }
        });
    };
}
//# sourceMappingURL=members-actions.js.map